/**
 * HubSpot Consent API command helpers
 * https://developers.hubspot.com/docs/api-reference/latest/account/settings/consent-banner/consent-banner-api
 */

export interface HubSpotConsentState {
	functionalAccepted: boolean;
	analyticsAccepted: boolean;
	marketingAccepted: boolean;
}

export const shouldLoadHubSpotScript = (consent: HubSpotConsentState): boolean =>
	consent.functionalAccepted || consent.analyticsAccepted || consent.marketingAccepted;

export const buildConsentCommand = (consent: HubSpotConsentState): [string, HubSpotConsent] => [
	"setHubSpotConsent",
	{
		functionality: consent.functionalAccepted,
		analytics: consent.analyticsAccepted,
		advertisement: consent.marketingAccepted,
	},
];

// Tells HubSpot to delete already-set tracking cookies and treat the visitor
// as new on the next page load. Sent on consent withdrawal.
export const buildRevokeCommand = (): [string] => ["revokeCookieConsent"];

// Returns true if the user has globally opted out of tracking via the browser:
// either Sec-GPC (modern, legally binding under CCPA) or the legacy DNT signal.
// When true, treat the visitor as if alwaysLoad were false — don't load HubSpot
// until they have explicitly consented.
export const shouldHonorDoNotTrack = (nav: {
	globalPrivacyControl?: boolean;
	doNotTrack?: string | null;
}): boolean => nav.globalPrivacyControl === true || nav.doNotTrack === "1";

/**
 * Determine which commands to push to _hsp based on consent state.
 *
 * Modes:
 * - Default (alwaysLoad: false): script only loads after consent. Pushes
 *   setHubSpotConsent on grant; pushes revokeCookieConsent on withdrawal
 *   (only if consent was previously granted).
 * - alwaysLoad: true: script loads immediately with necessary-only consent.
 *   Always pushes setHubSpotConsent so HubSpot reflects the current state.
 *   Pushes revokeCookieConsent in addition when consent transitions to none
 *   after having been granted, to delete already-set tracking cookies.
 */
export const getConsentCommands = (
	consent: HubSpotConsentState,
	hasGrantedConsent: boolean,
	options: { alwaysLoad?: boolean } = {}
): HubSpotCommand[] => {
	const { alwaysLoad = false } = options;
	const consentGiven = shouldLoadHubSpotScript(consent);
	const commands: HubSpotCommand[] = [];

	if (alwaysLoad || consentGiven) {
		commands.push(buildConsentCommand(consent));
	}

	if (!consentGiven && hasGrantedConsent) {
		commands.push(buildRevokeCommand());
	}

	return commands;
};
